import clsx from 'clsx'
import { Button, Container, Grid, Icon, Typography } from 'gu-ui-kit'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'
import { Link } from 'react-router-dom'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import Logo from 'layouts/subcomponents/Logo'
import styles from 'components/Header/header.module.scss'
import TicketsCounter from 'components/Header/subcomponents/TicketsCounter'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import { buildLandingPath } from 'utils/buildLandingPath'
import BalanceWidget from 'components/Drawer/subcomponents/BalanceWidget'
import PropTypes from 'prop-types'
import { SET_DRAWER_STATE } from 'modules/App/types'
import { selectAppDrawerState, selectAppLocale } from 'modules/App/store/selectors'
import { useAuthState } from 'modules/Auth/hooks/useAuthState'

const Header = ({ className = '', noToggler = false }) => {
  const { isAuthenticated } = useAuthState()
  const drawerState = useSelector(selectAppDrawerState)
  const locale = useSelector(selectAppLocale)
  const pathGen = useGeneratePathI18n()
  const matched = useRouteMatch()
  const breakpoints = useBreakpoint()
  const dispatch = useDispatch()
  const { t } = useTranslation(['common'])

  const isRegisterForm = useMemo(() => matched.path.match(/\/register/), [matched])

  return (
    <Container
      display={'flex'}
      component={'header'}
      className={clsx(styles['header'], styles['header_logged'], 'layout-tile', className)}
    >
      <Container
        display={'flex'}
        flexAlign={'center'}
        flexJustify={'space-between'}
        className={styles['header__width-wrapper']}
      >
        <Grid
          fluid={false}
          component={'nav'}
          className={styles['header__nav']}
          gap={16}
          flexDirection={'row'}
          wrap={false}
        >
          <Container className={styles['header__logo']}>
            <Logo />
          </Container>

          {isAuthenticated && !noToggler && (
            <>
              <Button
                className={styles['header__sidebar-toggle']}
                variant={'ghost'}
                grey
                onPointerDown={(e) => {
                  e.preventDefault()

                  dispatch({
                    type: SET_DRAWER_STATE,
                    payload: {
                      ...drawerState,
                      opened: !drawerState.opened,
                    },
                  })
                }}
                noLabel
                size={'s'}
                startIcon={<Icon name={drawerState.opened ? 'close' : 'burger'} />}
              />
            </>
          )}
        </Grid>

        <Grid
          wrap={false}
          gap={16}
          display={'flex'}
          flexJustify={'flex-end'}
          flexAlign={'center'}
        >
          {isAuthenticated ? (
            <>
              <Grid
                gap={16}
                wrap={false}
                fluid={false}
                flexAlign={'center'}
              >
                <BalanceWidget
                  minimalistic={!breakpoints.sm}
                  className={styles['header__balance-widget']}
                  small={true}
                />

                {breakpoints.sm && (
                  <>
                    <Button
                      component={'a'}
                      size={'s'}
                      variant={'ghost'}
                      grey
                      color={'Interface-Eight'}
                      startIcon={<Icon name={'question'} />}
                      target={'_blank'}
                      href={buildLandingPath(locale, ROUTES.FAQ)}
                    />
                    <TicketsCounter />
                  </>
                )}
              </Grid>
            </>
          ) : (
            <>
              <Typography
                variant={'Body-15'}
                color={'Label-Secondary'}
                noMargin
              >
                {isRegisterForm
                  ? t('common::header.has_account')
                  : t('common::header.have_no_account')}
              </Typography>
              <Link to={pathGen(isRegisterForm ? ROUTES.AUTH_LOGIN : ROUTES.AUTH_REGISTRATION)}>
                <Button
                  component={'span'}
                  className={clsx(styles['button'], styles['button-add'], {
                    [styles['button-add_compact']]: !breakpoints.xl,
                  })}
                  size={'s'}
                  variant={'primary'}
                >
                  {t(
                    isRegisterForm
                      ? 'common::header.login_button_label'
                      : 'common::header.register_button_label',
                  )}
                </Button>
              </Link>
            </>
          )}
        </Grid>
      </Container>
    </Container>
  )
}

export default Header

Header.propTypes = {
  className: PropTypes.string,
  noToggler: PropTypes.bool,
}
